import React, { useState } from 'react';
import { Alert, Card, CardBody, Col, Row } from 'reactstrap';
import notify from '../../../../../../utils/notify';
import TenderNotClosedAlert from '../Components/TenderNotClosedAlert';
import CargoDeliveryTable, {
  useDeliveryParcels,
} from '../../../../../FreightCost/component/CargoDeliveryTable';
import { downloadParcelFreightExcel } from '../../../../../FreightCost/utils/FreightCostHelpers';
import { getAuthToken } from '../../../../../../auth/tokenvalidation';
import { formatDateToDDMMYYYY } from '../../../../../../utils/dateformatter';
import {
  displayValue,
  formatProposalDeadline,
  FREIGHT_COST_COLUMNS,
  getContractPeriodProps,
  getContractTypeLabel,
  getFreightCostRows,
  getProcurementTypeLabel,
  canAccessEvaluationOperationalData,
  getEvaluationOperationalLockReason,
  isFreightCostAvailable,
} from '../../../../utils/tenderEvaluationHelpers';
import ActionButton from '../../../../../../Components/Common/ActionButton';
import {
  SkeletonCard,
  SkeletonTable,
} from '../../../../../../Components/Common/Skeleton';

const InfoField = ({ label, value, col = 4 }) => (
  <Col xs={6} md={col}>
    <div className='border-start border-2 border-primary ps-3'>
      <div className='text-muted small fw-semibold mb-1'>{label}</div>
      <p className='text-dark fs-13 mb-0 lh-base'>{displayValue(value)}</p>
    </div>
  </Col>
);

const Freight = ({ procurement, procId, isLoadingProcurement = false }) => {
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const token = getAuthToken();
  const procurementId = procurement?.id || procId;
  const {
    response: parcelResponse,
    isLoading: isLoadingParcels,
    error: parcelsError,
  } = useDeliveryParcels(procurementId, token);

  const lockReason = getEvaluationOperationalLockReason(procurement);
  const canAccessData = canAccessEvaluationOperationalData(procurement);
  const isFreightAvailable = isFreightCostAvailable(procurement);
  const periodProps = getContractPeriodProps(procurement);

  const exportParcelFreightToExcel = () => {
    const rows = getFreightCostRows(parcelResponse);
    if (!rows.length) {
      notify.info('No FOB incoterm data available to export.');
      return;
    }

    setIsExportingExcel(true);
    try {
      downloadParcelFreightExcel(
        rows,
        'Parcel_Freight_Cost.xlsx',
        FREIGHT_COST_COLUMNS
      );
    } catch (error) {
      notify.apiError(error, 'export parcel data to Excel');
    } finally {
      setIsExportingExcel(false);
    }
  };

  if (isLoadingProcurement && !procurement) {
    return (
      <>
        <SkeletonCard lines={4} className='mb-4' />
        <SkeletonTable rows={3} columns={FREIGHT_COST_COLUMNS.length} />
      </>
    );
  }

  if (!canAccessData) {
    return (
      <Card className='border shadow-sm mb-4'>
        <CardBody>
          <div className='mb-4'>
            <h5 className='fw-bold text-dark mb-1'>Freight Cost (for FOB)</h5>
            <p className='text-muted small mb-0'>
              Ringkasan freight cost untuk delivery term FOB.
            </p>
          </div>
          <TenderNotClosedAlert
            featureLabel='Freight Cost'
            reason={lockReason}
          />
        </CardBody>
      </Card>
    );
  }

  if (!isFreightAvailable) {
    return (
      <Card className='border shadow-sm mb-4'>
        <CardBody>
          <div className='mb-4'>
            <h5 className='fw-bold text-dark mb-1'>Freight Cost (for FOB)</h5>
            <p className='text-muted small mb-0'>
              Ringkasan freight cost untuk delivery term FOB.
            </p>
          </div>
          <Alert
            color='warning'
            className='d-flex align-items-start gap-2 mb-0'
          >
            <i className='ri-information-line fs-20 mt-1'></i>
            <div>
              <h5 className='alert-heading fs-14 fw-bold mb-1'>
                Freight Cost Not Available
              </h5>
              <p className='mb-0 fs-13'>
                Freight Cost belum berstatus Available/Released. Data freight
                dapat ditampilkan setelah Freight Cost di-release.
              </p>
            </div>
          </Alert>
        </CardBody>
      </Card>
    );
  }

  return (
    <Row>
      <Col xs={12}>
        <Card className='border shadow-sm mb-4'>
          <CardBody>
            <div className='mb-3'>
              <h5 className='fw-semibold mb-1'>Request Freight Availability</h5>
              <p className='text-muted small mb-0'>
                Freight cost and availability request details for this
                procurement.
              </p>
            </div>

            <hr className='my-3 border-secondary opacity-25' />

            <Row className='g-3'>
              <InfoField
                label='Freight Request Number'
                value={procurement?.freightRequestNumber}
              />
              <InfoField
                label='Freight Request Date'
                value={
                  procurement?.freightRequestDate
                    ? formatDateToDDMMYYYY(procurement.freightRequestDate)
                    : null
                }
              />
            </Row>
          </CardBody>
        </Card>

        <Card className='border shadow-sm mb-4'>
          <CardBody>
            <div className='mb-3'>
              <h5 className='fw-semibold mb-1'>Procurement Information</h5>
              <p className='text-muted small mb-0'>
                Procurement details linked to this freight request.
              </p>
            </div>

            <hr className='my-3 border-secondary opacity-25' />

            <Row className='g-3'>
              <InfoField
                label='Procurement Type'
                value={procurement?.procurementTypeHeaderLabel}
              />
              <InfoField
                label='Title Procurement'
                value={procurement?.titleProcurement}
              />
              <InfoField
                label='Proc Type'
                value={getProcurementTypeLabel(procurement?.procurementType)}
              />
              <InfoField
                label='Contract Type'
                value={getContractTypeLabel(procurement?.contractType)}
              />
              <InfoField
                label={periodProps.startLabel}
                value={periodProps.startValue}
              />
              {periodProps.endLabel && (
                <InfoField
                  label={periodProps.endLabel}
                  value={periodProps.endValue}
                />
              )}
              <InfoField
                label='Submission Deadline (Jakarta Local Time)'
                value={formatProposalDeadline(
                  procurement?.proposalSubmissionDeadline
                )}
                col={6}
              />
              <InfoField
                label='Description'
                value={procurement?.description}
                col={12}
              />
            </Row>
          </CardBody>
        </Card>

        <Card className='border shadow-sm mb-4'>
          <CardBody>
            <Alert
              color='info'
              className='d-flex align-items-start gap-2 border-0 bg-light text-primary'
            >
              <i className='ri-information-line flex-shrink-0 mt-1'></i>
              <span className='small'>
                The detailed procurement plan information below can be
                downloaded in Excel format to be used as an attachment in the
                submission of the Freight Cost and Availability Request
                Memorandum.
              </span>
            </Alert>

            <div className='d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3'>
              <div>
                <h5 className='fw-semibold mb-1'>Parcel</h5>
                <p className='text-muted small mb-0'>
                  Delivery terms mapped from procurement planning.
                </p>
              </div>
              <ActionButton
                variant='secondary'
                size='sm'
                icon='ri-file-excel-2-line'
                disabled={isLoadingParcels}
                loading={isExportingExcel}
                onClick={exportParcelFreightToExcel}
              >
                Export to Excel
              </ActionButton>
            </div>

            <hr className='my-2 border-secondary opacity-25' />

            <CargoDeliveryTable
              response={parcelResponse}
              loading={isLoadingParcels}
              error={parcelsError}
            />
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default Freight;
